import { createDirectus, rest, readItems, readItem, createItem, updateItem, deleteItems, staticToken, importFile, aggregate } from '@directus/sdk'
import { unstable_cache } from 'next/cache'
import { FAQItem } from './content-analyzers'
import { logger } from './logger'
import { withRetry } from './api-retry'
import { findBannedDashes, FAQ_MAX } from './content-guards'
import { REDIRECTED_BLOG_SLUGS } from '@/data/blog-redirects.mjs'

// ---------------------------------------------------------------------------
// Shared (cross-instance) data cache for listing-feeding reads.
//
// unstable_cache stores results in Vercel's data cache keyed by the parts
// below and invalidated by revalidateTag from /api/revalidate (Directus
// publish webhook). The module-level Map caches further down remain only as
// per-instance fallbacks (peekBlogPostCache last-known-good) — they must
// never be READ inside a cached fetcher, or a revalidateTag purge would be
// instantly repopulated with stale Map data.

/** Serialize options into a stable cache key: sorted keys, undefined dropped
 *  (plain JSON.stringify is insertion-order-sensitive and collapses undefined,
 *  which would produce silent key collisions/misses). */
function stableOptionsKey(options?: object): string {
  if (!options) return '{}'
  const source = options as Record<string, unknown>
  const sorted: Record<string, unknown> = {}
  for (const key of Object.keys(source).sort()) {
    if (source[key] !== undefined) sorted[key] = source[key]
  }
  return JSON.stringify(sorted)
}

/** Run a STRICT fetcher (one that THROWS on Directus failure) through
 *  unstable_cache. Semantics:
 *  - Success — including a legitimately empty result — is cached, so
 *    deletions/emptied listings propagate instead of pinning old data.
 *  - Failure throws, so an outage is never stored; on a cold miss the
 *    onError fallback answers this request uncached, and on a stale-entry
 *    background refresh Next keeps serving the last good value.
 *  The fetcher passed here must NOT be one of the error-swallowing public
 *  functions — those would cache an outage's empty result as success. */
async function withSharedCache<T>(
  keyParts: string[],
  opts: { tags: string[]; revalidate: number },
  strictFetcher: () => Promise<T>,
  onError: (err: unknown) => T
): Promise<T> {
  try {
    return await unstable_cache(strictFetcher, keyParts, opts)()
  } catch (err) {
    return onError(err)
  }
}

// Directus configuration
const DIRECTUS_URL = process.env.NEXT_PUBLIC_DIRECTUS_URL || 'http://localhost:8055'
const DIRECTUS_TOKEN = process.env.DIRECTUS_TOKEN || ''

// Create Directus client
export const directus = createDirectus(DIRECTUS_URL)
  .with(rest())
  .with(staticToken(DIRECTUS_TOKEN)) // Re-enabling authentication

// Content types interfaces
export interface DirectusProduct {
  id: string
  name: string
  description: string
  price: number | string
  category: string
  image: string
  timing: string
  featured: boolean
  best_seller: boolean
  affiliate_url: string
  status: 'published' | 'draft'
  date_created: string
  /**
   * Optional explicit brand. When set, takes priority over the prefix-heuristic
   * in `extractBrand`. Anthony to add this field to the Directus `products`
   * collection (string, optional) and backfill via PAAPI lookup.
   */
  brand?: string
  /**
   * CSV multi-selects (Directus `cast-csv` → string[]) driving city/grass →
   * product matching in `scoreProduct`. `season_affinity`: 'warm' | 'cool' |
   * 'transition' | 'any'. `grass_types`: species slugs ('bermuda', 'tall-fescue',
   * …) or 'any'. Empty/undefined is treated as 'any' (universal) by the scorer.
   */
  season_affinity?: string[]
  grass_types?: string[]
}

export type ProductSortBy = 'default' | 'price-asc' | 'price-desc' | 'popular'

export interface DirectusBlogPost {
  id: string
  title: string
  slug: string
  excerpt: string
  content: string
  category: string
  author: string
  tags: string[]
  reading_time: number
  meta_title?: string // SEO-optimized title override for search results
  meta_description: string
  featured: boolean
  image: string
  status: 'published' | 'draft'
  date_created: string
  date_published: string
  date_modified?: string // Last modification date
  /** Dedicated alt text for the featured photo: subject + visible ID feature +
   *  context. Describes the PICTURE, not the article (the excerpt used to fill
   *  this slot and described the article). */
  image_alt?: string | null
  /** Map from in-body <img> src to { alt, caption, credit, license, sourceUrl }.
   *  Feeds per-image ImageObject nodes for body photos with no figcaption. */
  content_image_meta?: Record<string, unknown> | string | null
  topic_id?: string // Topic ID from topic cluster (for tracking)
  topic_source?: 'static' | 'generated' | 'custom' // Source of the topic
  faqs?: FAQItem[] // Generated FAQs stored as JSON
}

export interface DirectusFAQ {
  id: string
  question: string
  answer: string
  category: string
  order: number
  status: 'published' | 'draft'
}

export interface DirectusCompetitor {
  id: string | number
  domain: string
  name?: string
  sitemap_url?: string
  last_analyzed?: string
  status?: 'pending' | 'analyzing' | 'completed' | 'error'
  date_created: string
  notes?: string
}

export interface DirectusCompetitorTopic {
  id: string
  competitor_id: string | number
  url: string
  title: string
  pillar?: string
  category?: string
  keywords: string[]
  date_analyzed: string
}

// Schema type for Directus
export interface DirectusSchema {
  products: DirectusProduct[]
  blog_posts: DirectusBlogPost[]
  faqs: DirectusFAQ[]
  competitors: DirectusCompetitor[]
  competitor_topics: DirectusCompetitorTopic[]
}

/**
 * Fix common mojibake from Windows-1252 double-encoded as UTF-8
 */
function fixEncoding(text: string): string {
  if (!text) return text
  return text
    .replace(/â€™/g, "'")   // right single quote
    .replace(/â€˜/g, "'")   // left single quote
    .replace(/â€œ/g, '"')   // left double quote
    .replace(/â€\u009d/g, '"')   // right double quote
    .replace(/â€"/g, "-")   // em dash (no em-dashes per style)
    .replace(/â€"/g, "-")   // en dash
    .replace(/â€¦/g, "...") // ellipsis
    .replace(/\u00e2\u0080\u0099/g, "'")  // raw bytes variant
    .replace(/\u00e2\u0080\u0093/g, "-")
    .replace(/\u00e2\u0080\u0094/g, "-")
}

// Products functions
type GetProductsOptions = NonNullable<Parameters<typeof getProductsUncached>[0]>

/** Cached wrapper — see the shared-cache block at the top of this file.
 *  Search queries bypass (unbounded key cardinality); so do throwOnError
 *  callers, whose contract is a live failure signal, not a cached one. */
export async function getProducts(options?: GetProductsOptions): Promise<{ products: DirectusProduct[]; total: number }> {
  if (options?.search || options?.throwOnError) {
    return getProductsUncached(options)
  }
  return withSharedCache(
    ['directus-products', stableOptionsKey(options)],
    { tags: ['products'], revalidate: 3600 },
    // throwOnError makes failures throw (never cached); a successful empty
    // catalog result stays cacheable.
    () => getProductsUncached({ ...(options ?? {}), throwOnError: true }),
    (err) => {
      console.error('getProducts: serving empty fallback after cache-layer fetch failure:', err instanceof Error ? err.message : err)
      return { products: [] as DirectusProduct[], total: 0 }
    }
  )
}

async function getProductsUncached(options?: {
  limit?: number
  category?: string
  featured?: boolean
  best_seller?: boolean
  search?: string
  offset?: number
  sortBy?: ProductSortBy
  /** Skip the aggregate count query when total is not needed (e.g. recommendation flows). Defaults to true. */
  includeCount?: boolean
  /** Let request-facing routes surface an unavailable state instead of an empty catalog. */
  throwOnError?: boolean
}): Promise<{ products: DirectusProduct[]; total: number }> {
  try {
    const filter: any = { status: { _eq: 'published' } }

    if (options?.category) {
      filter.category = { _eq: options.category }
    }

    if (options?.featured === true) {
      filter.featured = { _eq: true }
    }

    if (options?.best_seller === true) {
      filter.best_seller = { _eq: true }
    }

    // Add search filter if provided - only search in product name
    if (options?.search) {
      filter.name = { _icontains: options.search }
    }

    // Preserve the legacy insertion order for callers that do not request a
    // catalog sort. The public products page passes `default` explicitly.
    let sort: string[] = ['date_created']
    switch (options?.sortBy) {
      case 'default':
        sort = ['-date_created']
        break
      case 'price-asc':
        sort = ['price', 'date_created']
        break
      case 'price-desc':
        sort = ['-price', 'date_created']
        break
      case 'popular':
        // This is an editorial ordering based on catalog flags, not a live sales
        // or click-through ranking. Keep the public label equally explicit.
        sort = ['-featured', '-best_seller', 'name']
        break
      default:
        break
    }

    const shouldCount = options?.includeCount !== false

    const itemsPromise = withRetry(() => directus.request(
      readItems('products', {
        filter,
        sort,
        limit: options?.limit || 50,
        offset: options?.offset || 0,
        fields: [
          'id',
          'name',
          'description',
          'price',
          'category',
          'image',
          'timing',
          'featured',
          'best_seller',
          'affiliate_url',
          'status',
          'date_created',
          'season_affinity',
          'grass_types'
        ]
      })
    ))

    const countPromise = shouldCount
      ? withRetry(() => directus.request(
          aggregate('products', {
            aggregate: { count: '*' },
            query: { filter }
          })
        )).catch(() => null)
      : Promise.resolve(null)

    const [response, countResponse] = await Promise.all([itemsPromise, countPromise])

    // Extract count from aggregate response (0 when count was skipped)
    const totalCount = countResponse?.[0]?.count ?? response.length

    // Fix mojibake encoding in product text fields
    const products = (response as DirectusProduct[]).map(p => ({
      ...p,
      name: fixEncoding(p.name),
      description: fixEncoding(p.description),
    }))

    return {
      products,
      total: typeof totalCount === 'string' ? parseInt(totalCount, 10) : totalCount
    }
  } catch (error) {
    console.error('Error fetching products:', error)
    if (options?.throwOnError) throw error
    return {
      products: [] as DirectusProduct[],
      total: 0
    }
  }
}

export async function getProductCategories() {
  return withSharedCache(
    ['directus-product-categories'],
    { tags: ['products'], revalidate: 3600 },
    () => getProductCategoriesUncached(true),
    (err) => {
      console.error('Error fetching product categories:', err)
      return [] as string[]
    }
  )
}

async function getProductCategoriesUncached(throwOnError = false) {
  try {
    const response = await withRetry(() => directus.request(
      readItems('products', {
        filter: { status: { _eq: 'published' } },
        fields: ['category']
      })
    ))

    // Extract unique categories
    const categories = [...new Set(response.map(product => product.category))]
    return categories
  } catch (error) {
    if (throwOnError) throw error
    console.error('Error fetching product categories:', error)
    return []
  }
}

/**
 * Fetch 2 sidebar product picks for a blog category from Directus.
 * Maps blog category → product categories, then pulls the most relevant products.
 */
export async function getSidebarProductsForCategory(
  blogCategory: string
): Promise<{ name: string; description: string; affiliateUrl: string; price: string; imageUrl?: string }[]> {
  const { getProductCategoriesForBlog } = await import('./blog-product-mapping')
  const productCategories = getProductCategoriesForBlog(blogCategory)

  try {
    // Pull a pool of matching products then pick 3 at random
    const pool = await directus.request(
      readItems('products', {
        filter: {
          status: { _eq: 'published' },
          category: { _in: productCategories },
        },
        limit: 20,
        fields: ['name', 'description', 'price', 'affiliate_url', 'image'],
      })
    ) as DirectusProduct[]

    // Shuffle and take 3
    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pool[i], pool[j]] = [pool[j], pool[i]]
    }
    const picks = pool.slice(0, 3)

    return picks.map(p => ({
      name: fixEncoding(p.name),
      description: fixEncoding(p.description),
      affiliateUrl: p.affiliate_url,
      price: `$${typeof p.price === 'string' ? p.price : p.price.toFixed(2)}`,
      imageUrl: p.image ? getDirectusImageUrl(p.image, 176, 176) : undefined,
    }))
  } catch (error) {
    console.error('Error fetching sidebar products:', error)
    return []
  }
}

export async function getProductBySlug(slug: string) {
  try {
    // Since we don't have a slug field in the products table, 
    // we'll need to implement this differently or remove this function
    console.warn('getProductBySlug called but products table does not have slug field')
    return null
  } catch (error) {
    console.error('Error fetching product by slug:', error)
    return null
  }
}

// Blog posts functions

/** Upper bound on tokens fed into the search filter. Each token adds an `_or`
 *  branch, so an unbounded paste would build a pathologically large filter. */
const MAX_SEARCH_TOKENS = 6

interface GetBlogPostsOptions {
  limit?: number
  category?: string
  featured?: boolean
  search?: string
  offset?: number
  sortBy?: 'newest' | 'oldest' | 'reading_time_asc' | 'reading_time_desc'
  readingTimeFilter?: 'quick' | 'medium' | 'long'
  dateRange?: 'week' | 'month' | 'year' | 'all'
  /** Internal: rethrow Directus failures instead of returning empty (used by
   *  the cache layer so an outage is never stored as a success). */
  throwOnError?: boolean
}

/** Cached wrapper — see the shared-cache block at the top of this file.
 *  Bypasses for search (unbounded key cardinality) and dateRange (the
 *  uncached fn computes the window from Date.now(), so a cached entry
 *  would freeze the window edge). */
export async function getBlogPosts(options?: GetBlogPostsOptions) {
  if (options?.search || (options?.dateRange && options.dateRange !== 'all') || options?.throwOnError) {
    return getBlogPostsUncached(options)
  }
  return withSharedCache(
    ['directus-blog-posts', stableOptionsKey(options)],
    { tags: ['blog-posts'], revalidate: 300 },
    () => getBlogPostsUncached({ ...(options ?? {}), throwOnError: true }),
    (err) => {
      console.error('getBlogPosts: serving empty fallback after cache-layer fetch failure:', err instanceof Error ? err.message : err)
      return { posts: [] as any[], total: 0 as number | string }
    }
  )
}

async function getBlogPostsUncached(options?: GetBlogPostsOptions) {
  try {
    // Validate Directus configuration
    if (!DIRECTUS_URL || DIRECTUS_URL === 'http://localhost:8055') {
      console.warn('Directus URL not configured or using default localhost. Blog posts may not load.')
    }
    if (!DIRECTUS_TOKEN) {
      console.warn('Directus token not configured. Authentication may fail.')
    }

    const filter: any = { status: { _eq: 'published' } }

    if (options?.category) {
      filter.category = { _eq: options.category }
    }

    if (options?.featured !== undefined) {
      filter.featured = { _eq: options.featured }
    }

    // Add reading time filter
    if (options?.readingTimeFilter) {
      switch (options.readingTimeFilter) {
        case 'quick':
          filter.reading_time = { _lte: 5 }
          break
        case 'medium':
          filter.reading_time = { _gte: 5, _lte: 10 }
          break
        case 'long':
          filter.reading_time = { _gte: 10 }
          break
      }
    }

    // Add date range filter
    if (options?.dateRange && options.dateRange !== 'all') {
      const now = new Date()
      let startDate: Date
      
      switch (options.dateRange) {
        case 'week':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
          break
        case 'month':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
          break
        case 'year':
          startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000)
          break
        default:
          startDate = new Date(0) // Beginning of time
      }
      
      filter.date_published = { _gte: startDate.toISOString() }
    }

    // Add search filter if provided - search in title, excerpt, and content.
    // `_icontains` matches a single literal substring, so a multi-word query
    // only hit posts where those words happened to be adjacent. Tokenize
    // instead: each token must appear in at least one field, and the tokens are
    // ANDed. hooks/use-content-pagination.ts tokenizes the same way, so the
    // client-side pass does not discard rows this filter returns.
    if (options?.search) {
      const tokens = Array.from(
        new Set(options.search.trim().toLowerCase().split(/\s+/).filter(Boolean))
      ).slice(0, MAX_SEARCH_TOKENS)

      if (tokens.length > 0) {
        filter._and = tokens.map(token => ({
          _or: [
            { title: { _icontains: token } },
            { excerpt: { _icontains: token } },
            { content: { _icontains: token } }
          ]
        }))
      }
    }

    // Determine sort order
    let sort: string[] = ['-date_published'] // Default: newest first
    if (options?.sortBy) {
      switch (options.sortBy) {
        case 'newest':
          sort = ['-date_published']
          break
        case 'oldest':
          sort = ['date_published']
          break
        case 'reading_time_asc':
          sort = ['reading_time', '-date_published']
          break
        case 'reading_time_desc':
          sort = ['-reading_time', '-date_published']
          break
      }
    }

    // Only fetch content field if explicitly requested (for search functionality)
    // For listing pages, we don't need the full content - it's huge and slows down loading
    const baseFields = [
      'id',
      'title',
      'slug',
      'excerpt',
      'category',
      'reading_time',
      'featured',
      'image',
      'status',
      'date_published',
      'date_modified',
      'author',
      // Listing cards render the featured photo; without image_alt they fall
      // back to the post title as alt text.
      'image_alt'
    ]

    // Add content only if search is being performed (needed for search matching)
    const fields = options?.search
      ? [...baseFields, 'content']
      : baseFields

    // Fetch posts and count in parallel for better performance
    const [response, countResponse] = await Promise.all([
      withRetry(() => directus.request(
        readItems('blog_posts', {
          filter,
          sort,
          limit: options?.limit || 50,
          offset: options?.offset || 0,
          fields
        })
      )),
      withRetry(() => directus.request(
        aggregate('blog_posts', {
          aggregate: { count: '*' },
          query: { filter }
        })
      )).catch(() => null)
    ])

    // Extract count from aggregate response
    const totalCount = countResponse?.[0]?.count ?? response.length

    return {
      posts: response,
      total: totalCount
    }
  } catch (error: any) {
    // Cache-layer callers need the raw failure signal — rethrow before the
    // swallow-and-return-empty path below.
    if (options?.throwOnError) throw error

    // Enhanced error logging with proper serialization
    // Extract error information safely to avoid circular reference issues
    let errorInfo: any = {}
    
    if (error instanceof Error) {
      errorInfo = {
        name: error.name,
        message: error.message,
        stack: error.stack?.split('\n').slice(0, 5).join('\n') // Limit stack trace
      }
    } else if (error && typeof error === 'object') {
      // Try to extract useful properties from Directus SDK errors
      const err = error as any
      errorInfo = {
        // Common Directus SDK error properties
        status: err.status,
        statusText: err.statusText,
        statusCode: err.statusCode,
        code: err.code,
        extensions: err.extensions,
        // Safely extract response data
        response: err.response ? {
          status: err.response.status,
          statusText: err.response.statusText,
          data: typeof err.response.data === 'string' 
            ? err.response.data.substring(0, 500) // Limit length
            : err.response.data
        } : undefined
      }
      
      // Remove undefined values
      Object.keys(errorInfo).forEach(key => {
        if (errorInfo[key] === undefined) delete errorInfo[key]
      })
    } else {
      errorInfo = { value: String(error) }
    }
    
    // Log with separate arguments to avoid serialization issues
    console.error('Error fetching blog posts:')
    if (Object.keys(errorInfo).length > 0) {
      console.error('Error details:', errorInfo)
    } else {
      console.error('Error (could not serialize):', String(error))
    }
    console.error('Request options:', JSON.stringify(options || {}))
    console.error('Directus URL:', DIRECTUS_URL || 'NOT CONFIGURED')
    console.error('Directus token:', DIRECTUS_TOKEN ? 'CONFIGURED' : 'NOT CONFIGURED')
    
    return {
      posts: [],
      total: 0
    }
  }
}

// Simple in-memory cache for blog posts to prevent duplicate fetching..
const blogPostCache = new Map<string, { data: any; timestamp: number }>()
const CACHE_TTL = 5 * 60 * 1000 // 5 minutes cache

// Read whatever is in the in-memory cache for `slug` without triggering a
// fetch and without enforcing the TTL. Used by the blog page render to serve
// last-known-good content when Directus is transiently unreachable, so a
// crawler never sees a 5xx response for a previously-healthy URL.
export function peekBlogPostCache(slug: string): any | null {
  return blogPostCache.get(slug)?.data ?? null
}

// Export function to clear cache (useful for testing)
export function clearBlogPostCache(slug?: string) {
  if (slug) {
    blogPostCache.delete(slug)
  } else {
    blogPostCache.clear()
  }
}

// --- Comparison hero images -------------------------------------------------
// Admin-managed hero image for /compare/[slug] pages, stored in the Directus
// `comparison_images` collection keyed by the comparison slug. The comparison
// text itself lives in code (data/comparisons.tsx); only the optional hero
// image is editable in the admin, mirroring the blog cover-image pattern.
export interface ComparisonImage {
  /** Directus file UUID — pass to getDirectusImageUrl(). */
  image: string
  /** Optional photo credit / attribution rendered as a caption. */
  image_credit?: string | null
}

const comparisonImageCache = new Map<string, { data: ComparisonImage | null; timestamp: number }>()

// Clear the comparison-image cache (used by on-demand revalidation).
export function clearComparisonImageCache(slug?: string) {
  if (slug) {
    comparisonImageCache.delete(slug)
  } else {
    comparisonImageCache.clear()
  }
}

/**
 * Fetch the admin-managed hero image for a /compare/[slug] page. Returns the
 * Directus file UUID (+ optional credit), or null when no row/image is set or
 * Directus is transiently unreachable. Never throws — the compare page renders
 * fine without a hero. Mirrors the blog cover-image fetch + 5-minute caching.
 */
export async function getComparisonImageBySlug(slug: string): Promise<ComparisonImage | null> {
  const cached = comparisonImageCache.get(slug)
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data
  }
  try {
    const rows = (await withRetry(() =>
      directus.request(
        readItems('comparison_images' as any, {
          filter: { slug: { _eq: slug } } as any,
          fields: ['image', 'image_credit'] as any,
          limit: 1,
        })
      )
    )) as any[]
    const row = rows?.[0]
    const result: ComparisonImage | null = row?.image
      ? { image: row.image as string, image_credit: row.image_credit ?? null }
      : null
    comparisonImageCache.set(slug, { data: result, timestamp: Date.now() })
    return result
  } catch (error: any) {
    logger.warn('Failed to fetch comparison image by slug', { slug, error: error?.message })
    // Serve last-known-good if we have it; otherwise null. Never throw.
    const stale = comparisonImageCache.get(slug)?.data ?? null
    comparisonImageCache.set(slug, { data: stale, timestamp: Date.now() })
    return stale
  }
}

// --- Guide hero images ------------------------------------------------------
// Admin-managed hero image for /guides/<slug> grass-guide pages, stored in the
// Directus `guide_images` collection keyed by the guide slug. The guide content
// itself lives in code (data/guides/*.ts); only the optional hero image is
// editable in the admin, mirroring the comparison_images pattern.
export interface GuideImage {
  /** Directus file UUID — pass to getDirectusImageUrl(). */
  image: string
  /** Optional photo credit / attribution (stored, not rendered on the hero). */
  image_credit?: string | null
}

const guideImageCache = new Map<string, { data: GuideImage | null; timestamp: number }>()

// Clear the guide-image cache (used by on-demand revalidation / admin edits).
export function clearGuideImageCache(slug?: string) {
  if (slug) {
    guideImageCache.delete(slug)
  } else {
    guideImageCache.clear()
  }
}

/**
 * Fetch the admin-managed hero image for a /guides/<slug> page. Returns the
 * Directus file UUID (+ optional credit), or null when no row/image is set or
 * Directus is transiently unreachable. Never throws — the guide page renders
 * fine on its brand-gradient fallback. Mirrors getComparisonImageBySlug.
 */
export async function getGuideImageBySlug(slug: string): Promise<GuideImage | null> {
  const cached = guideImageCache.get(slug)
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data
  }
  try {
    const rows = (await withRetry(() =>
      directus.request(
        readItems('guide_images' as any, {
          filter: { slug: { _eq: slug } } as any,
          fields: ['image', 'image_credit'] as any,
          limit: 1,
        })
      )
    )) as any[]
    const row = rows?.[0]
    const result: GuideImage | null = row?.image
      ? { image: row.image as string, image_credit: row.image_credit ?? null }
      : null
    guideImageCache.set(slug, { data: result, timestamp: Date.now() })
    return result
  } catch (error: any) {
    logger.warn('Failed to fetch guide image by slug', { slug, error: error?.message })
    // Serve last-known-good if we have it; otherwise null. Never throw.
    const stale = guideImageCache.get(slug)?.data ?? null
    guideImageCache.set(slug, { data: stale, timestamp: Date.now() })
    return stale
  }
}

/**
 * Fetch multiple published blog posts by slug, preserving the input slug order.
 * Missing slugs come back as `null` in their position so callers can render
 * placeholders or skip them. Trimmed fields — meant for cards/strips, not full posts.
 */
export async function getBlogPostsBySlugs(slugs: string[]): Promise<(DirectusBlogPost | null)[]> {
  if (slugs.length === 0) return []
  try {
    const response = await withRetry(() => directus.request(
      readItems('blog_posts', {
        filter: {
          slug: { _in: slugs },
          status: { _eq: 'published' },
        },
        limit: slugs.length,
        fields: [
          'id',
          'title',
          'slug',
          'excerpt',
          'category',
          'image',
          'image_alt',
          'date_published',
          'reading_time',
          'featured',
        ],
      })
    ))
    const bySlug = new Map<string, any>(
      (response as any[]).map((p) => [p.slug, p])
    )
    return slugs.map((s) => (bySlug.get(s) as DirectusBlogPost | undefined) ?? null)
  } catch (error) {
    logger.error('Failed to fetch blog posts by slugs', { slugs, error })
    return slugs.map(() => null)
  }
}

/** Cached wrapper — see the shared-cache block at the top of this file.
 *  Tagged ONLY blog-post-<slug> (not the broad blog-posts tag) so a listing
 *  purge doesn't dump every post entry. allowDrafts (admin preview) bypasses
 *  entirely — draft content must never enter the shared cache. A successful
 *  miss (deleted/unknown slug) caches null so deletions propagate; Directus
 *  FAILURES throw and are answered uncached with the peek last-known-good.
 *  The module Map is refreshed on every published read, HIT or MISS, so the
 *  peekBlogPostCache outage fallback stays alive. */
export async function getBlogPostBySlug(slug: string, allowDrafts: boolean = false) {
  if (allowDrafts) {
    return getBlogPostBySlugUncached(slug, true)
  }
  const post = await withSharedCache(
    ['directus-blog-post', slug],
    { tags: [`blog-post-${slug}`], revalidate: 3600 },
    () => getBlogPostBySlugUncached(slug, false, true),
    (err) => {
      logger.error('getBlogPostBySlug: cache-layer fetch failed, serving last-known-good', err instanceof Error ? err : undefined, { slug })
      return peekBlogPostCache(slug)
    }
  )
  if (post) {
    blogPostCache.set(slug, { data: post, timestamp: Date.now() })
  }
  return post
}

async function getBlogPostBySlugUncached(slug: string, allowDrafts: boolean = false, throwOnError: boolean = false) {
  try {
    // NOTE: no Map-cache read here. This function runs inside unstable_cache;
    // a stale Map hit would instantly repopulate a freshly revalidateTag'd
    // shared cache with old data (cache poisoning). Map WRITES below stay —
    // they feed peekBlogPostCache's outage fallback only.

    const filter: any = {
      slug: { _eq: slug }
    }
    
    if (!allowDrafts) {
      filter.status = { _eq: 'published' }
    }

    const response = await withRetry(() => directus.request(
      readItems('blog_posts', {
        filter,
        limit: 1,
        fields: [
          'id',
          'title',
          'slug',
          'excerpt',
          'content',
          'category',
          'author',
          'tags',
          'reading_time',
          'meta_title',
          'meta_description',
          'featured',
          'image',
          'status',
          'date_created',
          'date_published',
          'date_modified',
          'faqs',
          'image_alt',
          'content_image_meta'
        ]
      })
    ))

    const blogPost = response[0] || null

    // Parse FAQs from JSON if present
    if (blogPost) {
      // Debug: Log what we got from Directus
      logger.info('Fetched blog post from Directus', {
        slug,
        hasFaqsField: 'faqs' in blogPost,
        faqsType: typeof blogPost.faqs,
        faqsValue: blogPost.faqs ? (typeof blogPost.faqs === 'string' ? blogPost.faqs.substring(0, 100) : 'not a string') : 'null/undefined'
      })
      
      if (blogPost.faqs) {
        try {
          if (typeof blogPost.faqs === 'string') {
            blogPost.faqs = JSON.parse(blogPost.faqs)
            logger.info('Successfully parsed FAQs from JSON', {
              slug,
              faqsCount: Array.isArray(blogPost.faqs) ? blogPost.faqs.length : 0
            })
          } else if (Array.isArray(blogPost.faqs)) {
            // Already parsed
            logger.info('FAQs already in array format', {
              slug,
              faqsCount: blogPost.faqs.length
            })
          }
        } catch (parseError) {
          logger.warn('Failed to parse FAQs JSON', {
            slug,
            error: parseError instanceof Error ? parseError.message : 'Unknown error',
            faqsValue: typeof blogPost.faqs === 'string' ? blogPost.faqs.substring(0, 200) : String(blogPost.faqs)
          })
          blogPost.faqs = null
        }
      } else {
        logger.info('No FAQs field in blog post', { slug })
      }
    }
    
    // Cache the result (but only if we successfully parsed FAQs or confirmed
    // they don't exist). Draft reads must NOT enter the Map — peekBlogPostCache
    // serves it publicly during outages.
    if (blogPost && !allowDrafts) {
      blogPostCache.set(slug, { data: blogPost, timestamp: Date.now() })
      logger.info('Cached blog post', {
        slug,
        hasFaqs: !!blogPost.faqs,
        faqsCount: blogPost.faqs && Array.isArray(blogPost.faqs) ? blogPost.faqs.length : 0
      })
    }

    return blogPost
  } catch (error: any) {
    // Gracefully handle missing optional fields (faqs, meta_title, date_modified)
    const errorMessage = String(error?.message || error?.errors?.[0]?.message || '').toLowerCase()
    const hasFaqsError = errorMessage.includes('faqs')
    const hasMetaTitleError = errorMessage.includes('meta_title')
    const hasDateModifiedError = errorMessage.includes('date_modified')
    const is403 = error?.response?.status === 403 || error?.response?.statusCode === 403

    if (is403 && (hasFaqsError || hasMetaTitleError || hasDateModifiedError)) {
      // Retry without problematic fields
      try {
        const filter: any = {
          slug: { _eq: slug }
        }

        if (!allowDrafts) {
          filter.status = { _eq: 'published' }
        }

        const fields: string[] = [
          'id',
          'title',
          'slug',
          'excerpt',
          'content',
          'category',
          'author',
          'tags',
          'reading_time',
          ...(!hasMetaTitleError ? ['meta_title'] : []),
          'meta_description',
          'featured',
          'image',
          'image_alt',
          'content_image_meta',
          'status',
          'date_created',
          'date_published',
          ...(!hasDateModifiedError ? ['date_modified'] : [])
        ]

        // Only include faqs if it's not causing the error
        if (!hasFaqsError) {
          fields.push('faqs')
        }

        const response = await withRetry(() => directus.request(
          readItems('blog_posts', {
            filter,
            limit: 1,
            fields
          })
        ))

        const blogPost = response[0] || null

        // Parse FAQs from JSON if present
        if (blogPost && blogPost.faqs) {
          try {
            if (typeof blogPost.faqs === 'string') {
              blogPost.faqs = JSON.parse(blogPost.faqs)
            }
          } catch (parseError) {
            logger.warn('Failed to parse FAQs JSON in retry', {
              slug,
              error: parseError instanceof Error ? parseError.message : 'Unknown error'
            })
            blogPost.faqs = null
          }
        }
        
        if (blogPost && !allowDrafts) {
          blogPostCache.set(slug, { data: blogPost, timestamp: Date.now() })
        }
        return blogPost
      } catch (retryError: any) {
        // If retry failed due another optional field, do a final safe retry
        const retryErrorMessage = String(retryError?.message || retryError?.errors?.[0]?.message || '').toLowerCase()
        const retryHasFaqsError = retryErrorMessage.includes('faqs')
        const retryHasMetaTitleError = retryErrorMessage.includes('meta_title')
        const retryHasDateModifiedError = retryErrorMessage.includes('date_modified')
        const retryIs403 = retryError?.response?.status === 403 || retryError?.response?.statusCode === 403

        if (retryIs403 && (retryHasFaqsError || retryHasMetaTitleError || retryHasDateModifiedError)) {
          try {
            const fallbackFilter: any = {
              slug: { _eq: slug }
            }

            if (!allowDrafts) {
              fallbackFilter.status = { _eq: 'published' }
            }

            const fallbackResponse = await withRetry(() => directus.request(
              readItems('blog_posts', {
                filter: fallbackFilter,
                limit: 1,
                fields: [
                  'id',
                  'title',
                  'slug',
                  'excerpt',
                  'content',
                  'category',
                  'author',
                  'tags',
                  'reading_time',
                  'meta_description',
                  'featured',
                  'image',
                  'status',
                  'date_created',
                  'date_published'
                ]
              })
            ))

            const fallbackPost = fallbackResponse[0] || null
            if (fallbackPost && !allowDrafts) {
              blogPostCache.set(slug, { data: fallbackPost, timestamp: Date.now() })
            }
            return fallbackPost
          } catch (finalRetryError: any) {
            logger.error('Error fetching blog post by slug (final retry failed)', finalRetryError, {
              slug,
              originalError: errorMessage,
              retryError: retryErrorMessage
            })
            if (throwOnError) throw finalRetryError
            return null
          }
        }

        logger.error('Error fetching blog post by slug (retry failed)', retryError, {
          slug,
          originalError: errorMessage
        })
        if (throwOnError) throw retryError
        return null
      }
    }

    logger.error('Error fetching blog post by slug', error, {
      slug,
      errorMessage,
      status: error?.response?.status || error?.statusCode
    })
    if (throwOnError) throw error
    return null
  }
}

// Get blog post by ID (supports drafts)
export async function getBlogPostById(id: string | number, allowDrafts: boolean = true) {
  try {
    const filter: any = {
      id: { _eq: String(id) }
    }
    
    if (!allowDrafts) {
      filter.status = { _eq: 'published' }
    }

    const response = await directus.request(
      readItems('blog_posts', {
        filter,
        limit: 1,
        fields: [
          'id',
          'title',
          'slug',
          'excerpt',
          'content',
          'category',
          'author',
          'tags',
          'reading_time',
          'meta_title',
          'meta_description',
          'featured',
          'image',
          'status',
          'date_created',
          'date_published'
        ]
      })
    )

    return response[0] || null
  } catch (error: any) {
    // Gracefully handle missing meta_title field
    const errorMessage = String(error?.message || error?.errors?.[0]?.message || '').toLowerCase()
    const hasMetaTitleError = errorMessage.includes('meta_title')
    const is403 = error?.response?.status === 403 || error?.response?.statusCode === 403

    if (is403 && hasMetaTitleError) {
      try {
        const filter: any = {
          id: { _eq: String(id) }
        }

        if (!allowDrafts) {
          filter.status = { _eq: 'published' }
        }

        const response = await directus.request(
          readItems('blog_posts', {
            filter,
            limit: 1,
            fields: [
              'id',
              'title',
              'slug',
              'excerpt',
              'content',
              'category',
              'author',
              'tags',
              'reading_time',
              'meta_description',
              'featured',
              'image',
              'status',
              'date_created',
              'date_published'
            ]
          })
        )

        return response[0] || null
      } catch (retryError) {
        console.error('Error fetching blog post by ID (retry failed):', retryError)
        return null
      }
    }

    console.error('Error fetching blog post by ID:', error)
    return null
  }
}

// Cache for related posts
const relatedPostsCache = new Map<string, { data: any[]; timestamp: number }>()

// Tokens too generic to signal topical overlap between two lawn-care slugs.
// Without this list every "best-*-2026" listicle matches every other one.
const RELATED_SLUG_STOPWORDS = new Set([
  'the', 'a', 'an', 'and', 'or', 'of', 'in', 'on', 'for', 'to', 'with', 'your',
  'how', 'what', 'when', 'why', 'is', 'vs', 'best', 'top', 'guide', 'tips',
  'complete', 'ultimate', 'lawn', 'grass', 'care', 'yard', '2025', '2026',
])

function relatedSlugTokens(slug: string): Set<string> {
  return new Set(
    slug
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter((t) => t.length > 1 && !RELATED_SLUG_STOPWORDS.has(t) && !/^20\d{2}$/.test(t)),
  )
}

// Relevance-rank candidates against the current post. Recency-only ordering
// (the pre-2026-08 behavior) made the Milwaukee services post recommend
// Chicago/Atlanta/Dallas while Madison sat unused — with ads live, related
// posts are the primary pages/session lever, so topical fit matters.
// Scoring: shared tags dominate, slug-token overlap catches regions and
// topics tags miss ("milwaukee", "bermuda"), same-category is a mild prior,
// and recency only breaks ties.
function scoreRelatedPost(
  post: any,
  currentTokens: Set<string>,
  currentTags: Set<string>,
  category?: string,
): number {
  let score = 0

  const postTags: string[] = Array.isArray(post.tags) ? post.tags : []
  for (const tag of postTags) {
    if (currentTags.has(String(tag).toLowerCase())) score += 3
  }

  for (const token of relatedSlugTokens(post.slug || '')) {
    if (currentTokens.has(token)) score += 1
  }

  if (category && post.category === category) score += 2

  return score
}

export async function getRelatedBlogPosts(
  currentSlug: string,
  category?: string,
  limit: number = 3,
  currentTags?: string[],
) {
  try {
    // Tags are part of the key: the admin preview passes DRAFT tags for the
    // same slug, and without them live and preview would reuse each other's
    // ranked results for the cache TTL.
    const safeTags = Array.isArray(currentTags) ? currentTags : []
    const cacheKey = `${currentSlug}-${category}-${limit}-${safeTags.join(',')}`

    // Check cache first
    const cached = relatedPostsCache.get(cacheKey)
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return cached.data
    }

    const fetchPostsWithFallback = async (config: {
      filter: any
      sort: any[]
      limit: number
    }): Promise<any[]> => {
      try {
        return await withRetry(() => directus.request(
          readItems('blog_posts', {
            ...config,
            fields: [
              'id',
              'title',
              'slug',
              'excerpt',
              'category',
              'tags',
              'reading_time',
              'image',
              'image_alt',
              'date_published',
              'date_created'
            ]
          })
        ))
      } catch (error: any) {
        const errorMessage = error?.message || error?.errors?.[0]?.message || String(error || '')
        console.warn('Error fetching related posts:', errorMessage)
        return []
      }
    }

    // Candidate pool: same-category posts plus recent posts site-wide. The
    // cross-category pool matters for regional/topical matches that live in a
    // different category (e.g. a state-grass post relating to a city-services
    // post); both are recency-sorted only to bound the pool, not to rank it.
    const POOL_SIZE = 30
    const [categoryPosts, recentPosts] = await Promise.all([
      category
        ? fetchPostsWithFallback({
            filter: {
              slug: { _neq: currentSlug },
              category: { _eq: category },
              status: { _eq: 'published' }
            },
            sort: ['-date_published', '-date_created'],
            limit: POOL_SIZE
          })
        : Promise.resolve([]),
      fetchPostsWithFallback({
        filter: {
          slug: { _neq: currentSlug },
          status: { _eq: 'published' }
        },
        sort: ['-date_published', '-date_created'],
        limit: POOL_SIZE
      })
    ])

    // A 301'd slug is still `published` in Directus until the archive step runs,
    // so filter it out of the pool rather than emitting a link that costs a hop.
    const bySlug = new Map<string, any>()
    for (const post of [...categoryPosts, ...recentPosts]) {
      if (!post?.slug || bySlug.has(post.slug)) continue
      if (REDIRECTED_BLOG_SLUGS.has(post.slug)) continue
      bySlug.set(post.slug, post)
    }

    const currentTokens = relatedSlugTokens(currentSlug)
    const currentTagSet = new Set(safeTags.map((t) => String(t).toLowerCase()))

    const relatedPosts = [...bySlug.values()]
      .map((post) => ({ post, score: scoreRelatedPost(post, currentTokens, currentTagSet, category) }))
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score
        // Recency as tiebreaker only
        const aDate = Date.parse(a.post.date_published || a.post.date_created || '') || 0
        const bDate = Date.parse(b.post.date_published || b.post.date_created || '') || 0
        return bDate - aDate
      })
      .slice(0, limit)
      .map((entry) => entry.post)

    // Cache the result
    relatedPostsCache.set(cacheKey, { data: relatedPosts, timestamp: Date.now() })

    return relatedPosts
  } catch (error) {
    console.error('Error fetching related blog posts:', error)
    return []
  }
}

// FAQs functions
export async function getFAQs(options?: {
  limit?: number
  category?: string
}) {
  try {
    const filter: any = { status: { _eq: 'published' } }

    if (options?.category) {
      filter.category = { _eq: options.category }
    }

    const response = await directus.request(
      readItems('faqs', {
        filter,
        sort: ['order'],
        limit: options?.limit || 50,
        fields: [
          'id',
          'question',
          'answer',
          'category',
          'order',
          'status'
        ]
      })
    )

    return response
  } catch (error) {
    console.error('Error fetching FAQs:', error)
    return []
  }
}

export async function getFAQBySlug(slug: string) {
  try {
    // Since we don't have a slug field in the FAQ table, 
    // we'll need to implement this differently or remove this function
    console.warn('getFAQBySlug called but FAQ table does not have slug field')
    return null
  } catch (error) {
    console.error('Error fetching FAQ by slug:', error)
    return null
  }
}

// Get all unique categories from blog posts
export async function getBlogCategories() {
  return withSharedCache(
    ['directus-blog-categories'],
    { tags: ['blog-posts'], revalidate: 3600 },
    () => getBlogCategoriesUncached(true),
    // Outage fallback lives HERE (in the error path, after the cache layer)
    // so it can never be mistaken for real data and stored in the cache.
    (err) => {
      console.error('Error fetching blog categories:', err)
      return ["Grass Types", "Lawn Care Tips", "Watering", "Fertilizing", "Mowing & Maintenance", "Pest Control", "Seasonal Care", "Climate & Regional Tips", "Tech & Tools", "Problem Solving"]
    }
  )
}

async function getBlogCategoriesUncached(throwOnError = false) {
  try {
    const response = await directus.request(
      readItems('blog_posts', {
        filter: {
          status: { _eq: 'published' }
        },
        fields: ['category'],
        sort: ['category'],
        limit: -1  // Fetch ALL posts to get all categories
      })
    )

    // Extract unique categories and sort them
    const uniqueCategories = [...new Set(response.map(post => post.category))].sort()
    
    return uniqueCategories
  } catch (error) {
    if (throwOnError) throw error
    console.error('Error fetching blog categories:', error)
    return [] as string[]
  }
}

// Get category counts for blog posts
export async function getBlogCategoryCounts() {
  return withSharedCache(
    ['directus-blog-category-counts'],
    { tags: ['blog-posts'], revalidate: 3600 },
    () => getBlogCategoryCountsUncached(true),
    (err) => {
      console.error('Error fetching blog category counts:', err)
      return {} as Record<string, number>
    }
  )
}

async function getBlogCategoryCountsUncached(throwOnError = false) {
  try {
    const response = await directus.request(
      readItems('blog_posts', {
        filter: {
          status: { _eq: 'published' }
        },
        fields: ['category'],
        limit: -1  // Fetch ALL posts, not just first 100
      })
    )

    // Count posts per category
    const categoryCounts: Record<string, number> = {}
    response.forEach(post => {
      if (post.category) {
        categoryCounts[post.category] = (categoryCounts[post.category] || 0) + 1
      }
    })
    
    return categoryCounts
  } catch (error) {
    if (throwOnError) throw error
    console.error('Error fetching blog category counts:', error)
    return {}
  }
}

// Test connection function
export async function testDirectusConnection() {
  try {
    const response = await directus.request(
      readItems('products', {
        limit: 1,
        fields: ['id']
      })
    )
    return true
  } catch (error) {
    console.error('Directus connection test failed:', error)
    return false
  }
}

// Image URL helper function
export function getDirectusImageUrl(
  imageId: string | null | undefined,
  width?: number,
  height?: number,
  quality: number = 80,
  fit: 'cover' | 'contain' | 'inside' | 'outside' = 'cover'
): string {
  if (!imageId) {
    return '/placeholder.jpg' // Fallback image
  }

  // If it's already a full URL (Supabase or other), return as is
  if (imageId.startsWith('http')) {
    return imageId
  }

  // If it's a Directus file ID, use Directus asset URL
  const baseUrl = DIRECTUS_URL.replace('/api', '').replace(/\/+$/, '')
  let url = `${baseUrl}/assets/${imageId}`

  const params = new URLSearchParams()
  if (width) params.append('width', width.toString())
  if (height) params.append('height', height.toString())
  params.append('quality', quality.toString())
  params.append('fit', fit)

  if (params.toString()) {
    url += `?${params.toString()}`
  }

  return url
}

/**
 * Turn arbitrary text (a post slug, a title, a species name) into the
 * decorative filename segment of a canonical image URL. The segment is what
 * gives an indexed image URL its subject words, so it is worth keeping
 * readable, but it is never used to locate the asset.
 */
export function slugifyForImage(text: string | null | undefined): string {
  if (!text) return 'image'
  const slug = text
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)
    .replace(/-+$/, '')
  return slug || 'image'
}

/**
 * The one canonical URL for a Directus-backed content image.
 *
 * Every surface that names an image -- the on-page src, sitemap-image.xml,
 * ImageObject.contentUrl, Article/BlogPosting image, og:image -- must use this
 * exact string, or Google sees several URLs for one picture and consolidates on
 * none of them. Served by app/img/[assetId]/[filename]/route.ts.
 *
 * Non-Directus sources (Pexels/Unsplash/Supabase `image_url`) are returned
 * untouched: they are already same-origin-irrelevant third-party URLs and
 * proxying them is out of scope.
 */
export function getCanonicalImageUrl(
  imageIdOrUrl: string | null | undefined,
  descriptiveSlug: string | null | undefined,
  absolute: boolean = false
): string {
  if (!imageIdOrUrl) {
    return '/placeholder.jpg'
  }

  let assetId = imageIdOrUrl
  if (imageIdOrUrl.startsWith('http')) {
    const match = imageIdOrUrl.match(/\/assets\/([0-9a-fA-F-]{20,})/)
    if (!match) {
      // Third-party or Supabase URL: pass through unchanged.
      return imageIdOrUrl
    }
    assetId = match[1]
  }

  if (!/^[0-9a-fA-F-]{20,}$/.test(assetId)) {
    return imageIdOrUrl
  }

  const path = `/img/${assetId}/${slugifyForImage(descriptiveSlug)}.jpg`
  if (!absolute) return path

  const siteUrl = (
    process.env.NEXT_PUBLIC_SITE_URL || 'https://whatgrassisthis.com'
  ).replace(/\/+$/, '')
  return `${siteUrl}${path}`
}

// Cache for adjacent posts
const adjacentPostsCache = new Map<string, { data: any; timestamp: number }>()

const ADJACENT_POST_FETCH_LIMIT = 5

// Get adjacent blog posts for navigation - OPTIMIZED VERSION with caching
export async function getAdjacentBlogPosts(currentSlug: string, datePublished?: string) {
  try {
    // Check cache first
    const cached = adjacentPostsCache.get(currentSlug)
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return cached.data
    }

    let currentDate = datePublished

    // Only fetch the post's date if not provided
    if (!currentDate) {
      const currentPostResponse = await directus.request(
        readItems('blog_posts', {
          filter: {
            slug: { _eq: currentSlug },
            status: { _eq: 'published' }
          },
          fields: ['date_published'],
          limit: 1
        })
      )

      if (!currentPostResponse || currentPostResponse.length === 0) {
        const result = { previousPost: null, nextPost: null }
        adjacentPostsCache.set(currentSlug, { data: result, timestamp: Date.now() })
        return result
      }

      currentDate = currentPostResponse[0].date_published
    }

    // Get previous and next posts in parallel using efficient date-based queries
    const [previousPosts, nextPosts] = await Promise.all([
      // Previous post: latest post with date < current date
      directus.request(
        readItems('blog_posts', {
          filter: {
            date_published: { _lt: currentDate },
            status: { _eq: 'published' }
          },
          sort: ['-date_published'],
          // Over-fetch so a run of 301'd slugs still yields a live neighbour.
          limit: ADJACENT_POST_FETCH_LIMIT,
          fields: ['title', 'slug', 'image']
        })
      ),
      // Next post: earliest post with date > current date
      directus.request(
        readItems('blog_posts', {
          filter: {
            date_published: { _gt: currentDate },
            status: { _eq: 'published' }
          },
          sort: ['date_published'],
          limit: ADJACENT_POST_FETCH_LIMIT,
          fields: ['title', 'slug', 'image']
        })
      )
    ])

    const firstLivePost = (posts: any[]) =>
      (Array.isArray(posts) ? posts : []).find(
        (post) => post?.slug && !REDIRECTED_BLOG_SLUGS.has(post.slug)
      )

    const previousPost = firstLivePost(previousPosts)
    const nextPost = firstLivePost(nextPosts)

    const result = {
      previousPost: previousPost ? {
        title: previousPost.title,
        slug: previousPost.slug,
        image: previousPost.image || null
      } : null,
      nextPost: nextPost ? {
        title: nextPost.title,
        slug: nextPost.slug,
        image: nextPost.image || null
      } : null
    }

    // Cache the result
    adjacentPostsCache.set(currentSlug, { data: result, timestamp: Date.now() })
    
    return result
  } catch (error) {
    console.error('Error fetching adjacent blog posts:', error)
    return { previousPost: null, nextPost: null }
  }
}

// ============================================
// BLOG POST WRITE FUNCTIONS
// ============================================

// Common SEO stop words to remove from slugs for better ranking
// Based on 2025 SEO best practices: shorter slugs (3-5 words, ≤60 chars) rank higher
const SLUG_STOP_WORDS = new Set([
  'a', 'an', 'the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for',
  'of', 'with', 'by', 'from', 'as', 'is', 'was', 'are', 'were', 'been',
  'be', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could',
  'should', 'may', 'might', 'must', 'shall', 'can', 'need', 'dare', 'ought',
  'used', 'your', 'yours', 'my', 'mine', 'our', 'ours', 'their', 'theirs',
  'its', 'it', 'he', 'she', 'him', 'her', 'his', 'hers', 'we', 'us', 'they', 'them',
  'this', 'that', 'these', 'those', 'what', 'which', 'who', 'whom', 'whose',
  'how', 'why', 'when', 'where', 'if', 'then', 'so', 'than', 'too', 'very',
  'just', 'only', 'also', 'even', 'more', 'most', 'other', 'some', 'any',
  'no', 'not', 'all', 'each', 'every', 'both', 'few', 'many', 'much', 'own'
])

// Helper: Generate URL-friendly slug from title
// Optimized for SEO: removes stop words, enforces length limit
export function slugify(
  text: string,
  options?: { removeStopWords?: boolean; maxLength?: number }
): string {
  const { removeStopWords = true, maxLength = 60 } = options || {}

  let slug = text.toLowerCase().trim()

  // Split into words for stop word removal
  if (removeStopWords) {
    const words = slug.split(/\s+/)
    const filtered = words.filter(word => {
      // Remove special chars from word before checking stop words
      const cleanWord = word.replace(/[^\w]/g, '')
      return cleanWord.length > 0 && !SLUG_STOP_WORDS.has(cleanWord)
    })
    // Keep at least 2 words even if they're stop words
    slug = filtered.length >= 2 ? filtered.join(' ') : words.slice(0, 3).join(' ')
  }

  return slug
    .replace(/[^\w\s-]/g, '')      // Remove special characters
    .replace(/[\s_-]+/g, '-')       // Replace spaces/underscores with hyphens
    .replace(/^-+|-+$/g, '')        // Remove leading/trailing hyphens
    .substring(0, maxLength)        // Enforce max length (SEO best practice: ≤60 chars)
    .replace(/-+$/, '')             // Clean trailing hyphen from truncation
}

// Helper: Calculate reading time (words per minute)
// Using 500 WPM for online reading (faster than print due to skimming)
export function calculateReadingTime(content: string): number {
  const wordsPerMinute = 500
  const textContent = content.replace(/<[^>]*>/g, '').trim() // Strip HTML tags
  const wordCount = textContent.split(/\s+/).filter(word => word.length > 0).length
  return Math.max(1, Math.ceil(wordCount / wordsPerMinute)) // Minimum 1 minute
}

// Helper: Check if a slug already exists
async function blogPostSlugExists(slug: string): Promise<boolean> {
  try {
    const response = await directus.request(
      readItems('blog_posts', {
        filter: { slug: { _eq: slug } },
        limit: 1,
        fields: ['id']
      })
    )
    return response.length > 0
  } catch (error) {
    console.error('Error checking slug existence:', error)
    return false
  }
}

// Create a new blog post in Directus
export interface CreateBlogPostData {
  title: string
  slug?: string // Optional, will auto-generate if not provided
  excerpt?: string
  content: string
  category: string
  author?: string
  tags?: string[]
  meta_title?: string // SEO-optimized title for search results
  meta_description?: string
  featured?: boolean
  image?: string | null
  image_url?: string | null // Original stock-photo URL (for cross-article dedup)
  status?: 'published' | 'draft'
  date_published?: string | null // ISO date string
  topic_id?: string // Topic ID from topic cluster
  topic_source?: 'static' | 'generated' | 'custom' // Source of the topic
  faqs?: FAQItem[] // Generated FAQs to store as JSON
}

export async function createBlogPost(data: CreateBlogPostData): Promise<DirectusBlogPost | null> {
  const { logger } = await import('./logger')
  
  // Immediate console output to verify function is being called
  console.log('\n🔵 createBlogPost CALLED')
  console.log(`   Title: "${data.title}"`)
  console.log(`   Directus URL: ${process.env.NEXT_PUBLIC_DIRECTUS_URL || 'NOT SET'}`)
  console.log(`   Has Token: ${!!process.env.DIRECTUS_TOKEN}`)

  // Content guard: hard-stop on banned dashes (project "no em-dashes" rule),
  // warn on FAQ overflow. Prevents the violation class that required a manual
  // fix-up script (see lib/content-guards.ts). Placed before the try so the
  // throw propagates to the caller instead of being swallowed into a null return.
  const createDashes = findBannedDashes(data.content)
  if (createDashes.count > 0) {
    throw new Error(
      `createBlogPost: "${data.title}" content has ${createDashes.count} banned dash(es) (em/en dash). ` +
        `Project rule: no em-dashes in content. Samples: ${createDashes.samples.join(' | ')}. ` +
        `Fix the source or run it through normalizeDashes() before saving.`
    )
  }
  if (data.faqs && data.faqs.length > FAQ_MAX) {
    logger.warn(
      `createBlogPost: "${data.title}" has ${data.faqs.length} FAQs (recommended ceiling ${FAQ_MAX})`
    )
  }
  
  try {
    logger.info('📝 Starting createBlogPost', {
      title: data.title,
      hasContent: !!data.content,
      contentLength: data.content?.length || 0,
      category: data.category,
      hasImage: !!data.image,
      directusUrl: process.env.NEXT_PUBLIC_DIRECTUS_URL,
      hasToken: !!process.env.DIRECTUS_TOKEN
    })
    
    // Auto-generate slug if not provided
    let finalSlug = data.slug || slugify(data.title)
    logger.info('Generated initial slug', { slug: finalSlug, title: data.title })
    
    // Ensure slug is unique (append number if needed)
    let counter = 1
    const baseSlug = finalSlug
    while (await blogPostSlugExists(finalSlug)) {
      finalSlug = `${baseSlug}-${counter}`
      counter++
      logger.info('Slug exists, trying new slug', { newSlug: finalSlug, counter })
      // Safety check to prevent infinite loops
      if (counter > 100) {
        finalSlug = `${baseSlug}-${Date.now()}`
        logger.warn('Slug conflict resolution hit max iterations, using timestamp', { finalSlug })
        break
      }
    }
    
    // Check for near-duplicate titles (normalized comparison)
    try {
      const normalizedTitle = data.title.toLowerCase().replace(/[^a-z0-9]/g, '')
      const existingPosts = await directus.request(
        readItems('blog_posts', {
          filter: { status: { _eq: 'published' } },
          fields: ['id', 'title', 'slug'],
          limit: 500
        })
      )
      const duplicates = existingPosts.filter((p: any) =>
        p.title.toLowerCase().replace(/[^a-z0-9]/g, '') === normalizedTitle
      )
      if (duplicates.length > 0) {
        logger.warn('⚠️ Possible duplicate title detected', {
          newTitle: data.title,
          existingMatches: duplicates.map((d: any) => ({ id: d.id, title: d.title, slug: d.slug }))
        })
      }
    } catch (dupeCheckError: any) {
      logger.warn('Duplicate title check failed (non-blocking)', { error: dupeCheckError.message })
    }

    // Calculate reading time
    const readingTime = calculateReadingTime(data.content)
    logger.info('Calculated reading time', { readingTime, contentLength: data.content?.length || 0 })
    
    // Prepare post data
    // Strip HTML tags from excerpt before saving
    const cleanExcerpt = data.excerpt 
      ? data.excerpt.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim()
      : ''
    
    const postData: any = {
      title: data.title,
      slug: finalSlug,
      excerpt: cleanExcerpt,
      content: data.content,
      category: data.category,
      author: data.author || 'Lawn Care Expert',
      tags: data.tags || [],
      reading_time: readingTime,
      meta_title: data.meta_title || '',
      meta_description: data.meta_description || '',
      featured: data.featured || false,
      status: data.status || 'draft',
      date_created: new Date().toISOString(),
      date_published: data.date_published || null
    }
    
    // Handle image field - Directus expects UUID for file relations, not URLs.
    // URL-typed values are routed to image_url instead so they're still tracked
    // for cross-article deduplication.
    if (data.image) {
      const isUrl = data.image.startsWith('http://') || data.image.startsWith('https://')
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(data.image)

      if (isUuid) {
        postData.image = data.image
      } else if (isUrl) {
        // Fallback: caller passed a URL (Directus import failed). Persist it on
        // image_url so dedup can still see it.
        if (!data.image_url) {
          postData.image_url = data.image
        }
      } else {
        console.warn(`⚠️ Image format not recognized: ${data.image}`)
      }
    }

    // Source stock-photo URL (separate from the Directus file UUID) — used for
    // cross-article deduplication so the next article doesn't re-pick the same
    // image from Pexels/Unsplash/Pixabay/Openverse.
    if (data.image_url) {
      postData.image_url = data.image_url
    }
    
    // Only include topic_id and topic_source if they have values
    // This allows the schema to work even if these fields don't exist in Directus yet
    if (data.topic_id) {
      postData.topic_id = data.topic_id
    }
    if (data.topic_source) {
      postData.topic_source = data.topic_source
    }
    
    // Store FAQs as JSON if provided
    if (data.faqs && data.faqs.length > 0) {
      postData.faqs = JSON.stringify(data.faqs)
      logger.info('Including FAQs in post data', {
        faqsCount: data.faqs.length
      })
      console.log(`\n✅ INCLUDING FAQs IN POST DATA: ${data.faqs.length} FAQs for "${data.title}"`)
    } else {
      console.log(`\n⚠️  NO FAQs TO SAVE: faqs=${data.faqs} (type: ${typeof data.faqs}, length: ${data.faqs?.length || 0})`)
      logger.warn('No FAQs provided to createBlogPost', {
        title: data.title,
        hasFaqs: !!data.faqs,
        faqsType: typeof data.faqs,
        faqsLength: data.faqs?.length || 0
      })
    }
    
    logger.info('📤 Sending request to Directus', {
      collection: 'blog_posts',
      dataKeys: Object.keys(postData),
      dataSizes: {
        title: postData.title?.length || 0,
        content: postData.content?.length || 0,
        excerpt: postData.excerpt?.length || 0,
        tagsCount: postData.tags?.length || 0
      },
      status: postData.status,
      slug: postData.slug,
      hasFaqs: !!postData.faqs,
      faqsLength: postData.faqs ? JSON.parse(postData.faqs).length : 0
    })
    
    // Try to create the item - wrap in additional try/catch for better error visibility
    let response
    try {
      process.stdout.write('\n🔵 Attempting Directus createItem request...\n')
      response = await directus.request(
        createItem('blog_posts', postData)
      )
      process.stdout.write('✅ Directus request completed successfully\n')
    } catch (directusError: any) {
      // Check if error is due to missing faqs field
      const errorMessage = directusError?.message || directusError?.errors?.[0]?.message || ''
      const hasFaqsError = errorMessage.toLowerCase().includes('faqs')
      const is403 = directusError?.response?.status === 403 || directusError?.response?.statusCode === 403
      
      if (is403 && hasFaqsError && postData.faqs) {
        // Retry without FAQs field if it doesn't exist in schema
        logger.warn('FAQs field not found in Directus schema, retrying without FAQs', {
          title: data.title
        })
        const postDataWithoutFaqs = { ...postData }
        delete postDataWithoutFaqs.faqs
        
        try {
          response = await directus.request(
            createItem('blog_posts', postDataWithoutFaqs)
          )
          logger.warn('Article saved without FAQs - please add faqs field to blog_posts collection in Directus')
          process.stdout.write('✅ Directus request completed (without FAQs)\n')
        } catch (retryError: any) {
          // If retry also fails, throw original error
          process.stdout.write('\n⚠️ DIRECTUS REQUEST THREW ERROR IMMEDIATELY\n')
          process.stdout.write(`Error Type: ${typeof directusError}\n`)
          process.stdout.write(`Error Message: ${directusError?.message || 'No message'}\n`)
          process.stdout.write(`Error Name: ${directusError?.name || 'No name'}\n`)
          if (directusError?.response) {
            process.stdout.write(`HTTP Status: ${directusError.response?.status || 'N/A'}\n`)
          }
          console.error('Directus request error caught:', directusError)
          throw directusError
        }
      } else {
        // Log immediately before rethrowing
        process.stdout.write('\n⚠️ DIRECTUS REQUEST THREW ERROR IMMEDIATELY\n')
        process.stdout.write(`Error Type: ${typeof directusError}\n`)
        process.stdout.write(`Error Message: ${directusError?.message || 'No message'}\n`)
        process.stdout.write(`Error Name: ${directusError?.name || 'No name'}\n`)
        if (directusError?.response) {
          process.stdout.write(`HTTP Status: ${directusError.response?.status || 'N/A'}\n`)
        }
        console.error('Directus request error caught:', directusError)
        throw directusError // Re-throw to be caught by outer catch
      }
    }
    
    logger.info('✅ Successfully created blog post in Directus', {
      id: response?.id,
      slug: response?.slug,
      title: response?.title,
      status: response?.status,
      hasFaqs: !!postData.faqs,
      faqsCount: postData.faqs ? JSON.parse(postData.faqs).length : 0
    })
    
    // Also log to console for visibility
    if (postData.faqs) {
      const faqsCount = JSON.parse(postData.faqs).length
      console.log(`\n✅ FAQs saved to Directus: ${faqsCount} FAQs for article "${data.title}"`)
      console.log(`   Article ID: ${response?.id}`)
      console.log(`   Slug: ${response?.slug}`)
    } else {
      console.log(`\n⚠️  No FAQs to save for article "${data.title}"`)
      console.log(`   Check if article.faqs was passed to createBlogPost`)
    }
    
    // Invalidate cache for related queries
    blogPostCache.clear()
    
    return response as DirectusBlogPost
  } catch (error: any) {
    // Use process.stdout.write to ensure immediate output (console.error might be buffered)
    process.stdout.write('\n' + '='.repeat(80) + '\n')
    process.stdout.write('❌ DIRECTUS SAVE ERROR - CATCH BLOCK TRIGGERED\n')
    process.stdout.write('='.repeat(80) + '\n')
    
    // Also use console.error as fallback
    console.error('\n' + '='.repeat(80))
    console.error('❌ DIRECTUS SAVE ERROR')
    console.error('='.repeat(80))
    console.error(`Article: "${data.title}"`)
    console.error(`Error Message: ${error?.message || 'No error message'}`)
    console.error(`Error Name: ${error?.name || 'Unknown'}`)
    console.error(`Error Code: ${error?.code || 'N/A'}`)
    console.error(`Error Type: ${typeof error}`)
    
    // Try multiple ways to extract error details (Directus SDK error structure varies)
    const errorDetails: any = {
      message: error?.message,
      name: error?.name,
      code: error?.code,
      status: error?.status,
      statusCode: error?.statusCode,
      response: error?.response,
      request: error?.request,
      errors: error?.errors,
      extensions: error?.extensions,
      data: error?.data,
      cause: error?.cause
    }
    
    // Log Directus SDK specific error structure
    if (error?.errors && Array.isArray(error.errors)) {
      console.error('\n📋 Directus Errors Array:')
      error.errors.forEach((err: any, idx: number) => {
        console.error(`  ${idx + 1}. ${JSON.stringify(err, null, 2)}`)
      })
    }
    
    // Check for response data in multiple possible locations
    const responseData = error?.response?.data || error?.data || error?.body
    if (responseData) {
      console.error('\n📦 Response Data:')
      console.error(JSON.stringify(responseData, null, 2))
    }
    
    // Check for HTTP status
    const httpStatus = error?.response?.status || error?.status || error?.statusCode
    if (httpStatus) {
      console.error(`\n🔢 HTTP Status: ${httpStatus}`)
    }
    
    // Check for request details
    if (error?.request) {
      console.error('\n📤 Request Details:')
      console.error(`  URL: ${error.request?.url || error.request}`)
      console.error(`  Method: ${error.request?.method || 'N/A'}`)
    }
    
    // Full error object (stringified, limited size)
    try {
      const errorStr = JSON.stringify(errorDetails, null, 2)
      if (errorStr.length < 2000) {
        console.error('\n📄 Full Error Details:')
        console.error(errorStr)
      } else {
        console.error('\n📄 Full Error Details (truncated):')
        console.error(errorStr.substring(0, 2000) + '...')
      }
    } catch (e) {
      console.error('\n⚠️  Could not stringify error object')
    }
    
    // Check for common issues
    const errorMessage = (error?.message || '').toLowerCase()
    const hasFieldError = errorMessage.includes('field') || 
                         errorMessage.includes('column') ||
                         errorMessage.includes('schema') ||
                         (error?.errors?.some((e: any) => e?.message?.toLowerCase().includes('field')))
    
    if (hasFieldError) {
      console.error('\n⚠️  POSSIBLE ISSUE: Database schema mismatch')
      console.error('   The blog_posts collection may be missing fields.')
      console.error('   Check if these fields exist in Directus:')
      console.error('   - topic_id (string, nullable)')
      console.error('   - topic_source (string, nullable)')
    }
    
    // Check for auth errors
    if (httpStatus === 401 || httpStatus === 403 || errorMessage.includes('unauthorized') || errorMessage.includes('forbidden')) {
      console.error('\n🔐 POSSIBLE ISSUE: Authentication/Permissions')
      console.error('   Check your DIRECTUS_TOKEN environment variable')
      console.error(`   Directus URL: ${process.env.NEXT_PUBLIC_DIRECTUS_URL}`)
      console.error(`   Has Token: ${!!process.env.DIRECTUS_TOKEN}`)
    }
    
    console.error('='.repeat(80) + '\n')
    
    // Then log to structured logger
    logger.error('❌ Error creating blog post in Directus', error, {
      errorMessage: error?.message,
      errorName: error?.name,
      errorStack: error?.stack,
      title: data.title,
      category: data.category,
      directusUrl: process.env.NEXT_PUBLIC_DIRECTUS_URL,
      hasToken: !!process.env.DIRECTUS_TOKEN,
      errorCode: error?.code,
      errorResponse: responseData,
      errorStatus: httpStatus,
      errors: error?.errors,
      extensions: error?.extensions
    })
    
    // Before returning null, log one more time with maximum visibility
    console.error('\n🟥 createBlogPost RETURNING NULL - ERROR WAS CAUGHT')
    console.error(`   Article: "${data.title}"`)
    console.error(`   This means the Directus save failed. Check logs above for details.\n`)
    
    return null
  }
}

// Update an existing blog post
export interface UpdateBlogPostData {
  title?: string
  slug?: string
  excerpt?: string
  content?: string
  category?: string
  author?: string
  tags?: string[]
  reading_time?: number
  meta_description?: string
  featured?: boolean
  image?: string | null
  image_url?: string | null // Original stock-photo URL (for cross-article dedup)
  status?: 'published' | 'draft'
  date_published?: string | null
  faqs?: FAQItem[] // Generated FAQs to store as JSON
}

export async function updateBlogPost(
  id: string, 
  data: UpdateBlogPostData,
  options: { enforce?: boolean } = {}
): Promise<DirectusBlogPost | null> {
  // Content guard: warn on banned dashes / FAQ overflow so edits surface rule
  // violations without blocking legacy posts that may predate the rule. Pass
  // { enforce: true } (e.g. from the content engine) to hard-stop instead.
  // See lib/content-guards.ts.
  if (data.content) {
    const dashes = findBannedDashes(data.content)
    if (dashes.count > 0) {
      const msg = `updateBlogPost(${id}): content has ${dashes.count} banned dash(es) (em/en dash). Samples: ${dashes.samples.join(' | ')}`
      if (options.enforce) throw new Error(msg)
      logger.warn(`⚠️ ${msg}`)
    }
  }
  if (data.faqs && data.faqs.length > FAQ_MAX) {
    const msg = `updateBlogPost(${id}): ${data.faqs.length} FAQs exceeds recommended ceiling ${FAQ_MAX}`
    if (options.enforce) throw new Error(msg)
    logger.warn(`⚠️ ${msg}`)
  }

  try {
    // Recalculate reading time if content changed
    if (data.content) {
      data.reading_time = calculateReadingTime(data.content)
    }
    
    // Convert FAQs to JSON string if provided
    const updateData: any = { ...data }
    if (data.faqs && data.faqs.length > 0) {
      updateData.faqs = JSON.stringify(data.faqs)
      logger.info('Including FAQs in update data', {
        id,
        faqsCount: data.faqs.length
      })
    }
    
    // Regenerate slug if title changed (but slug wasn't explicitly provided)
    if (data.title && !data.slug) {
      const newSlug = slugify(data.title)
      
      // Get existing post to check current slug
      try {
        const existing = await directus.request(
          readItem('blog_posts', id, { fields: ['slug', 'title'] })
        )
        
        // Only update slug if title actually changed
        if (existing.title !== data.title) {
          let finalSlug = newSlug
          let counter = 1
          
          // Check if new slug conflicts with other posts
          while (await blogPostSlugExists(finalSlug)) {
            const conflictCheck = await directus.request(
              readItems('blog_posts', {
                filter: { slug: { _eq: finalSlug } },
                fields: ['id'],
                limit: 1
              })
            )
            // If the conflicting post is the one we're updating, use the new slug
            if (conflictCheck[0]?.id === id) {
              break
            }
            finalSlug = `${newSlug}-${counter}`
            counter++
            if (counter > 100) {
              finalSlug = `${newSlug}-${Date.now()}`
              break
            }
          }
          updateData.slug = finalSlug
        }
      } catch (error) {
        // If we can't fetch existing, just use the generated slug
        console.warn('Could not fetch existing post for slug check:', error)
        updateData.slug = slugify(data.title)
      }
    }
    
    const response = await directus.request(
      updateItem('blog_posts', id, updateData)
    )
    
    // Invalidate relevant caches
    blogPostCache.clear()
    relatedPostsCache.clear()
    adjacentPostsCache.clear()
    
    return response as DirectusBlogPost
  } catch (error: any) {
    console.error('Error updating blog post:', error)
    console.error('Error details:', {
      id,
      message: error.message,
      response: error.response?.data,
      status: error.response?.status
    })
    return null
  }
}

// Delete a blog post
export async function deleteBlogPost(id: string): Promise<boolean> {
  try {
    await directus.request(
      deleteItems('blog_posts', [id])
    )
    
    // Invalidate caches
    blogPostCache.clear()
    relatedPostsCache.clear()
    adjacentPostsCache.clear()
    
    return true
  } catch (error: any) {
    console.error('Error deleting blog post:', error)
    console.error('Error details:', {
      id,
      message: error.message,
      response: error.response?.data,
      status: error.response?.status
    })
    return false
  }
}

// Get all draft posts
export async function getDraftBlogPosts(limit: number = 50): Promise<DirectusBlogPost[]> {
  try {
    const response = await withRetry(() => directus.request(
      readItems('blog_posts', {
        filter: { status: { _eq: 'draft' } },
        sort: ['-date_created'],
        limit,
        fields: [
          'id',
          'title',
          'slug',
          'excerpt',
          'content',
          'category',
          'author',
          'date_created',
          'featured',
          'image',
          'tags'
        ]
      })
    ))
    return response as DirectusBlogPost[]
  } catch (error) {
    console.error('Error fetching draft posts:', error)
    return []
  }
}
